import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import { portalApi, PORTAL_TOKEN_KEY, downloadFile } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { StatusBadge, money, EmptyState, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CreditCard, Sun, Moon, LogOut, Loader2, Check, Plus, Receipt, FileText, LifeBuoy, ReceiptText, FileDown } from "lucide-react";

export default function Portal() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [ticketOpen, setTicketOpen] = useState(false);
  const [tf, setTf] = useState({ subject: "", priority: "medium", category: "General" });
  const hasToken = !!localStorage.getItem(PORTAL_TOKEN_KEY);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["portal-summary"], queryFn: async () => (await portalApi.get("/portal/summary")).data, enabled: hasToken, retry: 0,
  });

  const payMut = useMutation({
    mutationFn: (invoiceId) => portalApi.post(`/payments/checkout/invoice/${invoiceId}`, { origin_url: window.location.origin }),
    onSuccess: (res) => { window.location.href = res.data.checkout_url; },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not start payment"),
  });
  const acceptMut = useMutation({
    mutationFn: (qid) => portalApi.post(`/portal/quotations/${qid}/accept`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["portal-summary"] }); toast.success("Quotation accepted"); },
  });
  const ticketMut = useMutation({
    mutationFn: (body) => portalApi.post("/portal/tickets", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["portal-summary"] }); setTicketOpen(false); setTf({ subject: "", priority: "medium", category: "General" }); toast.success("Ticket raised"); },
  });

  const logout = () => { localStorage.removeItem(PORTAL_TOKEN_KEY); navigate("/"); };

  if (!hasToken || isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4 text-center">
        <div>
          <Logo className="mb-4 justify-center" />
          <p className="text-muted-foreground">No active portal session.</p>
          <p className="mt-1 text-sm text-muted-foreground">Please open the secure link shared by your account manager.</p>
          <Link to="/"><Button variant="outline" className="mt-4 rounded-full">Go home</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bc-glass px-4 lg:px-8">
        <Logo />
        <div className="flex items-center gap-2">
          <span className="hidden text-sm text-muted-foreground sm:block">{data?.company_name}</span>
          <Button variant="ghost" size="icon" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="portal-theme-toggle">
            <Sun className="h-5 w-5 dark:hidden" /><Moon className="hidden h-5 w-5 dark:block" />
          </Button>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="portal-logout"><LogOut className="mr-1.5 h-4 w-4" /> Sign out</Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl p-4 lg:p-8">
        <div className="mb-6">
          <h1 className="font-heading text-2xl font-bold">Welcome, {data?.customer?.name?.split(" ")[0]}</h1>
          <p className="text-sm text-muted-foreground">Your invoices, quotations and support in one place.</p>
        </div>

        {isLoading ? <Loading /> : (
          <Tabs defaultValue="invoices">
            <TabsList className="flex-wrap">
              <TabsTrigger value="invoices" data-testid="portal-tab-invoices">Invoices</TabsTrigger>
              <TabsTrigger value="quotations" data-testid="portal-tab-quotations">Quotations</TabsTrigger>
              <TabsTrigger value="tickets" data-testid="portal-tab-tickets">Support</TabsTrigger>
              <TabsTrigger value="receipts" data-testid="portal-tab-receipts">Receipts</TabsTrigger>
            </TabsList>

            <TabsContent value="invoices" className="mt-4">
              {data.invoices.length === 0 ? <EmptyState icon={Receipt} title="No invoices" description="You have no invoices right now." /> : (
                <Card className="overflow-x-auto"><Table>
                  <TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {data.invoices.map((inv) => (
                      <TableRow key={inv.id} data-testid={`portal-invoice-${inv.id}`}>
                        <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                        <TableCell>{money(inv.total)}</TableCell>
                        <TableCell><StatusBadge value={inv.status} /></TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button variant="ghost" size="icon" className="h-8 w-8" title="Download PDF"
                              onClick={() => downloadFile(`/portal/invoices/${inv.id}/pdf`, `invoice-${inv.invoice_number}.pdf`, portalApi).catch(() => toast.error("Could not download PDF"))}
                              data-testid={`portal-invoice-pdf-${inv.id}`}><FileDown className="h-4 w-4" /></Button>
                            {inv.status !== "paid" ? (
                              <Button size="sm" onClick={() => payMut.mutate(inv.id)} disabled={payMut.isPending} data-testid={`portal-pay-${inv.id}`}>
                                {payMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="mr-1 h-3.5 w-3.5" /> Pay Now</>}
                              </Button>
                            ) : <span className="text-xs text-success">Paid</span>}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table></Card>
              )}
            </TabsContent>

            <TabsContent value="quotations" className="mt-4">
              {data.quotations.length === 0 ? <EmptyState icon={FileText} title="No quotations" description="No quotations to review." /> : (
                <Card className="overflow-x-auto"><Table>
                  <TableHeader><TableRow><TableHead>Quote</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {data.quotations.map((q) => (
                      <TableRow key={q.id} data-testid={`portal-quote-${q.id}`}>
                        <TableCell className="font-medium">{q.quote_number}</TableCell>
                        <TableCell>{money(q.total)}</TableCell>
                        <TableCell><StatusBadge value={q.status} /></TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button variant="ghost" size="icon" className="h-8 w-8" title="Download PDF"
                              onClick={() => downloadFile(`/portal/quotations/${q.id}/pdf`, `quotation-${q.quote_number}.pdf`, portalApi).catch(() => toast.error("Could not download PDF"))}
                              data-testid={`portal-quote-pdf-${q.id}`}><FileDown className="h-4 w-4" /></Button>
                            {q.status !== "accepted" ? (
                              <Button size="sm" variant="outline" onClick={() => acceptMut.mutate(q.id)} disabled={acceptMut.isPending} data-testid={`portal-accept-${q.id}`}>
                                <Check className="mr-1 h-3.5 w-3.5" /> Accept
                              </Button>
                            ) : <span className="text-xs text-success">Accepted</span>}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table></Card>
              )}
            </TabsContent>

            <TabsContent value="tickets" className="mt-4">
              <div className="mb-3 flex justify-end">
                <Dialog open={ticketOpen} onOpenChange={setTicketOpen}>
                  <DialogTrigger asChild><Button className="rounded-full" data-testid="portal-new-ticket-btn"><Plus className="mr-1.5 h-4 w-4" /> Raise ticket</Button></DialogTrigger>
                  <DialogContent><DialogHeader><DialogTitle>Raise a support ticket</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <div className="space-y-1.5"><Label>Subject</Label><Input value={tf.subject} onChange={(e) => setTf({ ...tf, subject: e.target.value })} data-testid="portal-ticket-subject" /></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5"><Label>Priority</Label>
                          <Select value={tf.priority} onValueChange={(v) => setTf({ ...tf, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{["low", "medium", "high", "critical"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent></Select>
                        </div>
                        <div className="space-y-1.5"><Label>Category</Label>
                          <Select value={tf.category} onValueChange={(v) => setTf({ ...tf, category: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{["Technical", "Billing", "Service", "General"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                        </div>
                      </div>
                      <DialogFooter><Button onClick={() => ticketMut.mutate(tf)} disabled={ticketMut.isPending || !tf.subject} data-testid="portal-ticket-save">{ticketMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}</Button></DialogFooter>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              {data.tickets.length === 0 ? <EmptyState icon={LifeBuoy} title="No tickets" description="Raise a ticket and our team will help you." /> : (
                <div className="space-y-2">
                  {data.tickets.map((t) => (
                    <Card key={t.id} className="flex items-center justify-between p-4" data-testid={`portal-ticket-${t.id}`}>
                      <div><p className="font-medium">{t.subject}</p><p className="text-xs text-muted-foreground">{t.ticket_number} · {t.category}</p></div>
                      <div className="flex items-center gap-2"><StatusBadge value={t.priority} /><StatusBadge value={t.status} /></div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="receipts" className="mt-4">
              {data.receipts.length === 0 ? <EmptyState icon={ReceiptText} title="No receipts yet" description="Receipts appear here after successful payments." /> : (
                <Card className="overflow-x-auto"><Table>
                  <TableHeader><TableRow><TableHead>Receipt</TableHead><TableHead>Invoice</TableHead><TableHead>Amount</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {data.receipts.map((r) => (
                      <TableRow key={r.id} data-testid={`portal-receipt-${r.id}`}>
                        <TableCell className="font-medium">{r.receipt_number}</TableCell>
                        <TableCell>{r.invoice_number}</TableCell>
                        <TableCell>{money(r.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table></Card>
              )}
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}
